import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSeverity } from '@/src/components/interfaces';
import { CToast } from '@/src/components/c-toast/CToast';
import { notificationsStore } from '@/src/infrastructure/notifications/store';

export interface NotificationOptions {
  message: string;
  severity?: CSeverity;
  duration?: number;
}

interface NotificationItem {
  id: number;
  message: string;
  severity: CSeverity;
  exiting: boolean;
}

interface NotificationsContextValue {
  show(options: NotificationOptions): void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) throw new Error('useNotifications requires CProvider.');
  return context;
}

export function CNotificationsProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const idCounter = useRef(0);

  const remove = useCallback((id: number) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, exiting: true } : item));
    setTimeout(() => setItems(prev => prev.filter(item => item.id !== id)), 300);
  }, []);

  const show = useCallback((options: NotificationOptions) => {
    const id = ++idCounter.current;
    const duration = options.duration ?? 5000;
    const item: NotificationItem = {
      id,
      message: options.message,
      severity: options.severity ?? CSeverity.Info,
      exiting: false,
    };

    setItems(prev => [...prev, item]);
    setTimeout(() => remove(id), duration);
  }, [remove]);
  const contextValue = useMemo(() => ({ show }), [show]);

  useEffect(() => {
    notificationsStore.subscribe(show);
    return () => notificationsStore.unsubscribe();
  }, [show]);

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className="c-snackbar">
          {items.map(item => (
            <div key={item.id} className={`c-snackbar__item ${item.exiting ? 'c-snackbar__item--exit' : ''}`}>
              <CToast severity={item.severity} onClose={() => remove(item.id)}>
                {item.message}
              </CToast>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </NotificationsContext.Provider>
  );
}
